import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// Column definition for Excel export
export interface ExcelColumn {
  header: string;
  key: string;
  width?: number;
}

// Options for Excel export
export interface ExcelExportOptions {
  filename?: string;
  sheetName?: string;
  columns: ExcelColumn[];
  styleHeader?: boolean;
  totalRow?:
    | { key: string; label?: string }
    | { key: string; label?: string }[]
    | {
        keys: { key: string; label?: string }[];
        label?: string;
        singleRow: true;
      };
}

/**
 * Exports data to Excel file
 * @param data Array of data objects to export
 * @param options Export options including columns, filename, etc.
 * @returns Promise that resolves when export is complete
 */
export const exportToExcel = async <T extends Record<string, unknown>>(
  data: T[],
  options: ExcelExportOptions
): Promise<void> => {
  try {
    // Create a new workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(options.sheetName || "Sheet1");

    // Set columns
    worksheet.columns = options.columns;

    // Add data rows
    data.forEach((item) => {
      worksheet.addRow(item);
    });

    // Add total row(s) if requested
    if (options.totalRow && data.length > 0) {
      // Single row for multiple totals
      if (
        typeof options.totalRow === "object" &&
        "singleRow" in options.totalRow &&
        options.totalRow.singleRow
      ) {
        const totalLabelColIndex = 1;
        const totalRowObj = options.totalRow;
        const totalRow = worksheet.addRow({});
        totalRow.getCell(totalLabelColIndex).value =
          totalRowObj.label || "Total";
        totalRowObj.keys.forEach((col, idx) => {
          const colIndex =
            options.columns.findIndex((c) => c.key === col.key) + 1;
          const startCell = worksheet.getRow(2).getCell(colIndex).address;
          const endCell = worksheet
            .getRow(data.length + 1)
            .getCell(colIndex).address;
          totalRow.getCell(colIndex).value = {
            formula: `SUM(${startCell}:${endCell})`,
          };
        });
        totalRow.font = { bold: true };
      } else {
        // Multiple separate total rows (legacy)
        const totalRows = Array.isArray(options.totalRow)
          ? options.totalRow
          : [options.totalRow];
        totalRows.forEach((row, idx) => {
          if (!("key" in row)) return; // skip if not a legacy row
          const totalLabelColIndex = 1;
          const colIndex =
            options.columns.findIndex((col) => col.key === row.key) + 1;
          const startCell = worksheet.getRow(2).getCell(colIndex).address;
          const endCell = worksheet
            .getRow(data.length + 1)
            .getCell(colIndex).address;
          const totalRow = worksheet.addRow({});
          totalRow.getCell(totalLabelColIndex).value = row.label || "Total";
          totalRow.getCell(colIndex).value = {
            formula: `SUM(${startCell}:${endCell})`,
          };
          totalRow.font = { bold: true };
        });
      }
    }

    // Style the header row if requested
    if (options.styleHeader !== false) {
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };
    }

    // Generate the Excel file
    const buffer = await workbook.xlsx.writeBuffer();

    // Create a Blob from the buffer
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Generate filename with current date if not provided
    let filename = options.filename;
    if (!filename) {
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      filename = `export_${dateStr}.xlsx`;
    }

    // Save the file
    saveAs(blob, filename);
  } catch (error) {
    console.error("Export to Excel failed:", error);
    throw error;
  }
};

/**
 * Helper function to format date to 'dd-mm-yyyy'
 * @param dateString Date string to format
 * @returns Formatted date string
 */
export const formatDateForExcel = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};
